import TRUNCATE_UNITS from '@/utils/enum/TRUNCATE_UNITS';
import { formatBool, formatArrayToString } from '@/utils/shared';
import dateFormatter from '@/services/formatter_services/date-formatter';
import dateService from '@/services/date-service';
import { presentNumberToCurrencyTable } from '@/services/formatter_services/currency-formatter';

const INQUIRY_HEADERS = Object.freeze([
  { text: 'Dashboard.widgets.inquiries.headers.id', value: 'id' },
  { text: 'Common.type', value: 'inquiryType' },
  {
    text: 'Common.label',
    value: 'requestIdentification',
    truncateUnit: TRUNCATE_UNITS.STRING,
    truncateAfter: 20
  },
  {
    text: 'Common.company',
    value: 'company',
    truncateUnit: TRUNCATE_UNITS.STRING,
    truncateAfter: 20
  },
  { text: 'Common.customer', value: 'customer' },
  { text: 'Dashboard.widgets.inquiries.headers.processOwner', value: 'processOwner' },
  {
    text: 'Common.status',
    value: 'status'
  },
  {
    text: 'Dashboard.widgets.inquiries.headers.inquiryCreated',
    value: 'inquiryCreated',
    presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
  },
  {
    text: 'Dashboard.widgets.inquiries.headers.deadline',
    value: 'deadline',
    presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
  },
  {
    text: 'Dashboard.widgets.inquiries.headers.updatedAt',
    value: 'updatedAt',
    presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
  }
]);
const PROJECT_MANAGEMENT_WIDGETS = Object.freeze({
  PROJECT_WIDGETS: [
    {
      id: 'projectsOverview',
      moduleId: 'projectsOverview',
      path: 'projects',
      type: 'TABLE',
      getData: response => response.data.data?.map(data => data.data),
      headers: [
        {
          text: 'Common.matchcode',
          value: 'matchcode',
          truncateUnit: TRUNCATE_UNITS.STRING,
          truncateAfter: 10
        },
        {
          text: 'Dashboard.widgets.projectsOverview.headers.projectManager',
          value: 'projectManager'
        },
        { text: 'Common.label', value: 'label' },
        {
          text: 'Common.customer',
          value: 'customer',
          truncateUnit: TRUNCATE_UNITS.STRING,
          truncateAfter: 20
        },
        {
          text: 'Common.start',
          value: 'startDate',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
        },
        {
          text: 'Dashboard.widgets.projectsOverview.headers.endDateTarget',
          value: 'endDateTarget',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
        },
        {
          text: 'Common.end',
          value: 'projectEndActual',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
        },
        {
          text: 'Common.status',
          value: 'projectStatus'
        },
        {
          text: 'Dashboard.widgets.projectsOverview.headers.productivity',
          value: 'productivity'
        },
        {
          text: 'Dashboard.widgets.projectsOverview.headers.deleted',
          value: 'isDeleted',
          presentWith: formatBool
        },
        {
          text: 'Dashboard.widgets.projectsOverview.headers.participants',
          value: 'participants',
          presentWith: formatArrayToString
        }
      ],
      withRouterActionLink: true
    },
    {
      id: 'projectsAmount',
      path: 'projects',
      type: 'VALUE',
      getData: response => response.data.totalHits,
      moduleId: 'projectsOverview'
    }
  ],
  INQUIRY_WIDGETS: [
    {
      id: 'inquiries',
      moduleId: 'projectInquiries',
      path: 'inquiries',
      type: 'TABLE',
      getData: response => response.data.data?.map(data => data.data),
      headers: INQUIRY_HEADERS,
      withRouterActionLink: true
    },
    {
      id: 'inquiriesToday',
      moduleId: 'projectInquiries',
      path: 'inquiries',
      type: 'TABLE',
      defaultFilter: { createdAt: dateService.getToday() },
      getData: response => response.data.data?.map(data => data.data),
      headers: INQUIRY_HEADERS,
      withRouterActionLink: true
    },
    {
      id: 'inquiriesMonth',
      moduleId: 'projectInquiries',
      path: 'inquiries',
      type: 'TABLE',
      defaultFilter: { createdAt: dateService.getFirstDayOfCurrentMonth() },
      getData: response => response.data.data?.map(data => data.data),
      headers: INQUIRY_HEADERS,
      withRouterActionLink: true
    },
    {
      id: 'inquiriesAmount',
      path: 'inquiries',
      type: 'VALUE',
      getData: response => response.data.totalHits,
      moduleId: 'projectInquiries'
    },
    {
      id: 'inquiriesTodayAmount',
      path: 'inquiries',
      type: 'VALUE',
      defaultFilter: { createdAt: dateService.getToday() },
      getData: response => response.data.totalHits,
      moduleId: 'projectInquiries'
    },
    {
      id: 'inquiriesMonthAmount',
      path: 'inquiries',
      type: 'VALUE',
      defaultFilter: { createdAt: dateService.getFirstDayOfCurrentMonth() },
      getData: response => response.data.totalHits,
      moduleId: 'projectInquiries'
    }
  ],
  PRODUCT_WIDGETS: [
    {
      id: 'products',
      moduleId: 'products',
      path: 'products',
      type: 'TABLE',
      getData: response => response.data.data?.map(data => data.data),
      headers: [
        { text: 'Common.productNumber', value: 'productNumber'},
        { text: 'Common.matchcode', value: 'matchCode' },
        {
          text: 'Common.label',
          value: 'label'
        },
        { text: 'Common.producer',
          value: 'producer'
        },
        { text: 'Common.supplier',
          value: 'supplier'
        },
        { text: 'Dashboard.widgets.products.headers.baseUnit',
          value: 'baseUnit'
        },
        { text: 'Dashboard.widgets.products.headers.basePrice',
          value: 'basePrice',
          presentWith: presentNumberToCurrencyTable,
          alignValue: 'right',
        },
        { text: 'Dashboard.widgets.products.headers.categories',
          value: 'categoryNames',
          presentWith: formatArrayToString
        }
      ],
      withRouterActionLink: true
    },
    {
      id: 'productAmount',
      path: 'products',
      type: 'VALUE',
      getData: response => response.data.totalHits,
      moduleId: 'products'
    }
  ]
});

export default PROJECT_MANAGEMENT_WIDGETS;
